import { ExternalLink } from "lucide-react";
import {
    METHODOLOGY_PAPERS,
    METHODOLOGY_COMMUNITY_URL,
    METHODOLOGY_AUTHOR,
    getPaperBySlug,
} from "../data/methodology";

/*
 * Compact "DOI ↗" pill for inline placement inside prose paragraphs.
 * Mirrors the pill used inside the PublishedMethodology card so users
 * see one consistent citation affordance across the site.
 */
export function DoiBadge({ slug }) {
    const paper = getPaperBySlug(slug);
    if (!paper) return null;
    return (
        <a
            href={paper.url}
            target="_blank"
            rel="noopener noreferrer"
            aria-label={`Read ${paper.shortTitle} on Zenodo`}
            data-testid={`doi-badge-${paper.slug}`}
            className="doi-inline-badge no-underline inline-flex items-center gap-1.5 align-middle ml-1.5 px-2 py-0.5 rounded-md border border-[#00A3FF]/30 text-[10px] font-mono uppercase tracking-[0.12em] text-white/60 hover:text-[#49bef5] hover:border-[#00A3FF]/60 hover:-translate-y-[1px] transition-all whitespace-nowrap"
        >
            DOI
            <ExternalLink size={10} strokeWidth={2} />
        </a>
    );
}

/*
 * "Published Methodology" credibility card. Currently rendered inside
 * blog posts (filtered to the papers each post cites). Accepts an
 * optional `slugs` prop to filter which papers are shown; when omitted
 * it renders all four.
 */
export default function PublishedMethodology({ slugs = null, useFullTitle = false }) {
    const papers =
        slugs && slugs.length > 0
            ? slugs.map(getPaperBySlug).filter(Boolean)
            : METHODOLOGY_PAPERS;
    if (papers.length === 0) return null;

    return (
        <div
            id="published-methodology"
            data-testid="published-methodology"
            className="relative scroll-mt-24"
        >
            <div
                className="relative rounded-2xl border border-white/10 bg-white/[0.02] p-6 lg:p-8 overflow-hidden"
                style={{
                    background:
                        "radial-gradient(circle at 0% 0%, rgba(43,166,224,0.06), transparent 60%), rgba(255,255,255,0.015)",
                }}
            >
                {/* Left electric-blue accent line */}
                <div
                    className="absolute left-0 top-6 bottom-6 w-[2px] bg-[#00A3FF]/40"
                    aria-hidden
                />

                <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-4 mb-6">
                    <div>
                        <div className="text-[11px] uppercase tracking-[0.22em] font-mono text-white/55 mb-2">
                            <span className="text-[#00E6A0] mr-2">
                                {">"}
                            </span>
                            Published Methodology
                        </div>
                        <div className="text-white/75 text-[14px] lg:text-[15px] leading-relaxed">
                            TRT Plus Research · ORCID{" "}
                            <a
                                href={METHODOLOGY_AUTHOR.orcidUrl}
                                target="_blank"
                                rel="noopener noreferrer"
                                data-testid="published-methodology-orcid"
                                className="font-mono tabular-nums text-white/85 hover:text-[#49bef5] transition-colors"
                            >
                                {METHODOLOGY_AUTHOR.orcid}
                            </a>
                        </div>
                    </div>
                    <a
                        href={METHODOLOGY_COMMUNITY_URL}
                        target="_blank"
                        rel="noopener noreferrer"
                        data-testid="published-methodology-community"
                        className="inline-flex items-center gap-1.5 text-[11px] uppercase tracking-[0.16em] font-mono text-white/55 hover:text-[#49bef5] transition-colors self-start lg:self-end"
                    >
                        View community
                        <ExternalLink size={12} strokeWidth={2} />
                    </a>
                </div>

                <ul className="space-y-2.5 list-none pl-0 legal-prose-reset">
                    {papers.map((p) => (
                        <li
                            key={p.slug}
                            data-testid={`methodology-paper-${p.slug}`}
                            className="grid grid-cols-[auto_1fr_auto] items-center gap-3 lg:gap-5 py-2 border-t border-white/5 first:border-t-0"
                        >
                            <span className="text-[11px] font-mono text-white/40 tabular-nums">
                                {String(p.note).padStart(2, "0")}
                            </span>
                            <span className="text-white text-[14px] lg:text-[15px] leading-snug">
                                {useFullTitle ? p.title : p.shortTitle}
                            </span>
                            <a
                                href={p.url}
                                target="_blank"
                                rel="noopener noreferrer"
                                aria-label={`Read ${p.shortTitle} on Zenodo`}
                                className="doi-chip inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md border border-[#00A3FF]/25 text-[10px] font-mono uppercase tracking-[0.12em] text-white/60 hover:text-[#49bef5] hover:border-[#00A3FF]/60 hover:-translate-y-[1px] transition-all whitespace-nowrap"
                            >
                                DOI
                                <ExternalLink
                                    size={11}
                                    strokeWidth={2}
                                />
                            </a>
                        </li>
                    ))}
                </ul>
            </div>
        </div>
    );
}
